import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Radar, Sparkles, Zap, ArrowRight } from 'lucide-react';

// Bump to re-announce to all users
const WHATS_NEW_VERSION = '2026.04.23';
const STORAGE_KEY = 'prism_whats_new_seen_version';

const UPDATES = [
  {
    icon: Radar,
    title: 'Competitor Radar is live',
    body: 'Track competitor ads from Meta\'s Ad Library with keyword or advertiser suggestions — powered by Apify.',
    color: 'from-[#2EB4AC] to-cyan-500',
  },
  {
    icon: Sparkles,
    title: 'One-click AI insights',
    body: 'Analyze any competitor ad with GPT-4o Vision — theme, emotion, CTAs, colors, people, product visibility.',
    color: 'from-violet-500 to-fuchsia-500',
  },
  {
    icon: Zap,
    title: 'Smart filters & bulk delete',
    body: 'Choose static or video, sort by recent or impressions, select-delete multiple ads at once.',
    color: 'from-amber-500 to-orange-500',
  },
];

// === Modern AI mascot — neural-core orb ===
const AIMascot = () => (
  <div className="relative w-40 h-40 mx-auto" aria-hidden>
    {/* outer orbit — dashed, slow rotate */}
    <svg viewBox="0 0 160 160" className="absolute inset-0 w-full h-full" style={{ animation: 'spin 18s linear infinite' }}>
      <circle cx="80" cy="80" r="72" fill="none" stroke="url(#orbit-g)" strokeWidth="1" strokeDasharray="3 8" opacity="0.5" />
      <defs>
        <linearGradient id="orbit-g" x1="0" x2="1">
          <stop offset="0%" stopColor="#2EB4AC" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </linearGradient>
      </defs>
      {/* orbiting satellites */}
      <circle cx="80" cy="8" r="2.5" fill="#2EB4AC" />
      <circle cx="152" cy="80" r="1.5" fill="#8b5cf6" opacity="0.7" />
    </svg>

    {/* middle ring — counter-rotate */}
    <svg viewBox="0 0 160 160" className="absolute inset-5 w-[calc(100%-2.5rem)] h-[calc(100%-2.5rem)]" style={{ animation: 'spin 12s linear infinite reverse' }}>
      <circle cx="80" cy="80" r="60" fill="none" stroke="#0ea5e9" strokeWidth="1" strokeDasharray="1 6" opacity="0.35" />
      <circle cx="140" cy="80" r="2" fill="#0ea5e9" />
    </svg>

    {/* ambient core glow */}
    <div className="absolute inset-8 rounded-full bg-gradient-to-br from-[#2EB4AC]/40 via-cyan-400/30 to-violet-500/40 blur-2xl animate-pulse" />

    {/* CORE — hex / diamond core with inner neural nodes */}
    <div className="absolute inset-0 flex items-center justify-center" style={{ animation: 'bob 4s ease-in-out infinite' }}>
      <svg viewBox="0 0 100 100" className="w-20 h-20 drop-shadow-[0_0_20px_rgba(46,180,172,0.6)]">
        <defs>
          <linearGradient id="core-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0f172a" />
            <stop offset="100%" stopColor="#1e293b" />
          </linearGradient>
          <linearGradient id="core-edge" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#2EB4AC" />
            <stop offset="60%" stopColor="#0ea5e9" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
          <radialGradient id="core-inner" cx="50%" cy="45%">
            <stop offset="0%" stopColor="#2EB4AC" stopOpacity="0.9" />
            <stop offset="60%" stopColor="#0ea5e9" stopOpacity="0.3" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <filter id="core-glow">
            <feGaussianBlur stdDeviation="2" />
          </filter>
        </defs>

        {/* outer hex */}
        <polygon points="50,5 88,27 88,73 50,95 12,73 12,27"
          fill="url(#core-grad)" stroke="url(#core-edge)" strokeWidth="1.5" />

        {/* inner plasma */}
        <polygon points="50,15 80,32 80,68 50,85 20,68 20,32"
          fill="url(#core-inner)" opacity="0.85">
          <animate attributeName="opacity" values="0.6;1;0.6" dur="2.2s" repeatCount="indefinite" />
        </polygon>

        {/* neural nodes — animated connections */}
        <g opacity="0.95" filter="url(#core-glow)">
          {/* central brain-like node */}
          <circle cx="50" cy="50" r="3.5" fill="#2EB4AC">
            <animate attributeName="r" values="3.5;5;3.5" dur="1.8s" repeatCount="indefinite" />
            <animate attributeName="fill" values="#2EB4AC;#8b5cf6;#2EB4AC" dur="3.4s" repeatCount="indefinite" />
          </circle>
          {/* satellite nodes */}
          <circle cx="35" cy="36" r="1.8" fill="#67e8f9">
            <animate attributeName="opacity" values="0.4;1;0.4" dur="1.6s" repeatCount="indefinite" />
          </circle>
          <circle cx="65" cy="36" r="1.8" fill="#a78bfa">
            <animate attributeName="opacity" values="1;0.4;1" dur="1.6s" repeatCount="indefinite" />
          </circle>
          <circle cx="35" cy="64" r="1.8" fill="#a78bfa">
            <animate attributeName="opacity" values="0.4;1;0.4" dur="2s" begin="0.4s" repeatCount="indefinite" />
          </circle>
          <circle cx="65" cy="64" r="1.8" fill="#67e8f9">
            <animate attributeName="opacity" values="1;0.4;1" dur="2s" begin="0.4s" repeatCount="indefinite" />
          </circle>
          {/* lines connecting to center */}
          <line x1="50" y1="50" x2="35" y2="36" stroke="#2EB4AC" strokeWidth="0.6" opacity="0.5" />
          <line x1="50" y1="50" x2="65" y2="36" stroke="#8b5cf6" strokeWidth="0.6" opacity="0.5" />
          <line x1="50" y1="50" x2="35" y2="64" stroke="#8b5cf6" strokeWidth="0.6" opacity="0.5" />
          <line x1="50" y1="50" x2="65" y2="64" stroke="#2EB4AC" strokeWidth="0.6" opacity="0.5" />
        </g>

        {/* scan line sweep */}
        <rect x="15" y="-2" width="4" height="100" fill="url(#scan-line)" opacity="0.8">
          <animateTransform attributeName="transform" type="translate" from="-30 0" to="110 0" dur="2.6s" repeatCount="indefinite" />
        </rect>
        <defs>
          <linearGradient id="scan-line" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="transparent" />
            <stop offset="50%" stopColor="#2EB4AC" stopOpacity="0.5" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
        </defs>
      </svg>
    </div>

    {/* corner data sparkles */}
    {[
      { t: '12%', l: '18%', d: '0s', c: 'text-cyan-400' },
      { t: '18%', l: '78%', d: '0.7s', c: 'text-violet-400' },
      { t: '74%', l: '14%', d: '1.3s', c: 'text-[#2EB4AC]' },
      { t: '78%', l: '80%', d: '2s', c: 'text-amber-400' },
    ].map((p, i) => (
      <Sparkles key={i} className={`absolute w-2.5 h-2.5 ${p.c}`}
        style={{ top: p.t, left: p.l, animation: `twinkle 2.6s ease-in-out ${p.d} infinite` }} />
    ))}

    <style>{`
      @keyframes bob {
        0%, 100% { transform: translateY(0) rotate(0deg); }
        50% { transform: translateY(-4px) rotate(1.5deg); }
      }
      @keyframes twinkle {
        0%, 100% { opacity: 0.2; transform: scale(0.7); }
        50% { opacity: 1; transform: scale(1.15); }
      }
      @keyframes spin {
        from { transform: rotate(0); }
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

export default function WhatsNewModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem(STORAGE_KEY);
    if (seen === WHATS_NEW_VERSION) return;
    const t = setTimeout(() => setOpen(true), 700);
    return () => clearTimeout(t);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, WHATS_NEW_VERSION);
    setOpen(false);
  };

  const handleExplore = () => {
    handleDismiss();
    window.location.href = '/competitor-radar';
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleDismiss(); }}>
      <DialogContent
        className="max-w-md p-0 gap-0 border-0 shadow-2xl rounded-3xl overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-slate-900"
        data-testid="whats-new-modal"
      >
        {/* Subtle grid overlay */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

        {/* ambient blobs */}
        <div className="absolute -top-20 -left-16 w-56 h-56 rounded-full bg-[#2EB4AC]/20 blur-3xl" />
        <div className="absolute top-0 -right-20 w-56 h-56 rounded-full bg-violet-500/20 blur-3xl" />

        {/* Hero */}
        <div className="relative pt-8 pb-4">
          <AIMascot />
          <div className="text-center mt-4 px-6">
            <p className="text-[10px] font-bold tracking-[0.3em] text-[#2EB4AC] uppercase">PRISM · Release Notes</p>
            <h2 className="text-xl font-bold text-white mt-1.5">AI just got sharper</h2>
            <p className="text-[12px] text-slate-400 mt-1.5 max-w-[320px] mx-auto leading-relaxed">
              Three upgrades are live. Same PRISM, now powered by a deeper intelligence layer.
            </p>
          </div>
        </div>

        {/* Updates */}
        <div className="relative px-6 py-5 space-y-2.5">
          {UPDATES.map((u, i) => {
            const Icon = u.icon;
            return (
              <div
                key={i}
                className="flex items-start gap-3 p-3 rounded-xl border border-white/5 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/10 transition-colors"
                style={{ animation: `slideUp 0.4s ease-out ${0.15 + i * 0.08}s both` }}
              >
                <div className={`shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br ${u.color} flex items-center justify-center shadow-lg`}>
                  <Icon className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white leading-tight">{u.title}</p>
                  <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{u.body}</p>
                </div>
                <div className="shrink-0 text-[9px] font-bold text-[#2EB4AC] tracking-[0.2em] pt-1">NEW</div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="relative px-6 pb-6 pt-2 flex items-center justify-between gap-3">
          <button
            onClick={handleDismiss}
            className="text-xs font-medium text-slate-500 hover:text-slate-300 transition-colors"
            data-testid="whats-new-close-btn"
          >
            Close
          </button>
          <Button
            onClick={handleExplore}
            className="h-10 px-5 rounded-xl bg-gradient-to-r from-[#2EB4AC] to-cyan-500 hover:from-[#259993] hover:to-cyan-600 text-white font-semibold shadow-lg shadow-[#2EB4AC]/20"
            data-testid="whats-new-explore-btn"
          >
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            Try Competitor Radar
            <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
          </Button>
        </div>

        <style>{`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
